import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@mui/material';
import moment from 'moment';
import AppStyles from '../styles/AppStyles';
import SearchField from '../components/SearchField';
import { CardSkeleton } from '../components/SkeletonLoader';
import ErrorState from '../components/ErrorState';
import NoEntries from '../components/NoEntries';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:3000';

function Vaccination() {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [petId, setPetId] = useState(() => localStorage.getItem('currentPetId') || '');
  const queryClient = useQueryClient();

  // Load vaccination logs for selected pet via React Query
  const vaccinationsQuery = useQuery({
    queryKey: ['vaccinationLogs', petId],
    enabled: !!petId,
    queryFn: async () => {
      const token = localStorage.getItem('vetToken');
      const currentPetId = localStorage.getItem('currentPetId') || petId;
      if (!currentPetId) return [];
      const secResp = await fetch(`${API_BASE}/api/sections?petId=${encodeURIComponent(currentPetId)}&added=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const sections = await secResp.json();
      if (!secResp.ok) throw new Error(sections?.message || t('vaccination.failedToLoadSections'));
      const vaccSection = (sections || []).find(s => String(s.name || '').toLowerCase().includes('vaccin'));
      if (!vaccSection) return [];
      const logsResp = await fetch(`${API_BASE}/api/sections-vaccination?petId=${encodeURIComponent(currentPetId)}&sectionId=${encodeURIComponent(vaccSection.id)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const logsData = await logsResp.json();
      if (!logsResp.ok) throw new Error(logsData?.message || t('vaccination.failedToLoadLogs'));
      const normalized = (Array.isArray(logsData) ? logsData : []).map(l => ({
        id: l.id || l._id,
        petName: '-',
        vaccineName: l.vaccineName || '-',
        category: l.vaccineCategory || '-',
        dateGiven: l.date || l.createdAt,
        nextDueDate: l.nextDueDate || null,
        vaccineAdministrationType: l.vaccineAdministrationType || null,
        veterinarian: '-',
        lotNumber: '-',
        manufacturer: '-',
        status: '-',
        notes: l.notes || '',
        boosterRequired: false,
        boosterInterval: '-',
      }));
      return normalized;
    }
  });

  useEffect(() => {
    // Sync petId and invalidate cache on pet change
    const currentPetId = localStorage.getItem('currentPetId') || petId;
    if (currentPetId !== petId) setPetId(currentPetId || '');
    if (currentPetId) {
      queryClient.invalidateQueries({ queryKey: ['vaccinationLogs', currentPetId] });
    }
  }, [petId, queryClient]);

  // React to pet change
  useEffect(() => {
    const handlePetChange = () => {
      const currentPetId = localStorage.getItem('currentPetId');
      if (currentPetId && currentPetId !== petId) setPetId(currentPetId);
    };
    window.addEventListener('petChanged', handlePetChange);
    return () => window.removeEventListener('petChanged', handlePetChange);
  }, [petId]);

  // Filtered vaccinations by search term (vaccine name or category)
  const vaccinations = useMemo(() => vaccinationsQuery.data || [], [vaccinationsQuery.data]);
  const filteredVaccinations = useMemo(() => {
    const list = Array.isArray(vaccinations) ? vaccinations : [];
    const term = (searchTerm || '').trim().toLowerCase();
    if (!term) return list;
    return list.filter(v =>
      (v.vaccineName || '').toLowerCase().includes(term) ||
      (v.category || '').toLowerCase().includes(term)
    );
  }, [vaccinations, searchTerm]);

  // Status helpers (align with mobile VaccineCategoryCard)
  const today = useMemo(() => moment().startOf('day'), []);
  const WARNING_DAYS = 15;
  const isDueSoon = useCallback((date) => {
    if (!date) return false;
    const days = moment(date).diff(today, 'days');
    return days >= 0 && days <= WARNING_DAYS;
  }, [today]);
  const isExpired = useCallback((date) => {
    if (!date) return false;
    return !moment(date).isAfter(today, 'day');
  }, [today]);
  const getStatusStyle = (date) => {
    // Pastel, subtle pills matching app aesthetic
    if (!date) return { label: '-', bg: 'rgba(0,0,0,0.04)', fg: AppStyles.colors.text.primary, border: AppStyles.colors.border.light };
    if (isExpired(date)) {
      return {
        label: t('vaccination.overdue'),
        bg: 'rgba(244, 67, 54, 0.12)', // red 500 @12%
        fg: '#b71c1c',
        border: 'rgba(244, 67, 54, 0.35)'
      };
    }
    if (isDueSoon(date)) {
      return {
        label: t('vaccination.dueSoon'),
        bg: 'rgba(255, 152, 0, 0.12)', // amber 500 @12%
        fg: '#8d6e00',
        border: 'rgba(255, 152, 0, 0.35)'
      };
    }
    return {
      label: t('vaccination.upToDate'),
      bg: 'rgba(76, 175, 80, 0.12)', // green 500 @12%
      fg: '#2e7d32',
      border: 'rgba(76, 175, 80, 0.35)'
    };
  };

  // Group by vaccination category, then within each category show ALL logs.
  // Older logs for the same administration type are marked as Renewed.
  const groupedByCategory = useMemo(() => {
    const byCat = new Map();
    (filteredVaccinations || []).forEach((v) => {
      const cat = v.category || '-';
      const at = v.vaccineAdministrationType || t('vaccination.unknown');
      if (!byCat.has(cat)) byCat.set(cat, new Map());
      const typeMap = byCat.get(cat);
      const arr = typeMap.get(at) || [];
      arr.push(v);
      typeMap.set(at, arr);
    });
    // Convert to array for rendering
    return Array.from(byCat.entries()).map(([category, typeMap]) => {
      const items = [];
      typeMap.forEach((arr) => {
        const sorted = arr.slice().sort((a, b) => new Date(b.dateGiven) - new Date(a.dateGiven));
        sorted.forEach((item, idx) => {
          items.push({ ...item, _isRenewed: idx > 0 });
        });
      });
      items.sort((a, b) => new Date(b.dateGiven) - new Date(a.dateGiven));
      return { category, items };
    }).sort((a, b) => a.category.localeCompare(b.category));
  }, [filteredVaccinations, t]);

  // Derive lists for summary cards per category AND administration type (match mobile logic)
  const { overdueVaccines, dueSoonVaccines } = useMemo(() => {
    const groups = new Map();
    (vaccinations || []).forEach(v => {
      const category = v.category || '-';
      const at = v.vaccineAdministrationType || t('vaccination.unknown');
      const key = `${category}__${at}`;
      const curr = groups.get(key);
      const currDate = curr ? new Date(curr.dateGiven).getTime() : 0;
      const thisDate = v.dateGiven ? new Date(v.dateGiven).getTime() : 0;
      if (!curr || thisDate > currDate) {
        groups.set(key, { category, vaccineAdministrationType: at === 'Unknown' ? null : at, dateGiven: v.dateGiven, nextDueDate: v.nextDueDate });
      }
    });
    const entries = Array.from(groups.values());
    const overdue = entries
      .filter(e => e.nextDueDate && isExpired(e.nextDueDate))
      .sort((a, b) => new Date(a.nextDueDate) - new Date(b.nextDueDate));
    const next = entries
      .filter(e => e.nextDueDate && !isExpired(e.nextDueDate) && moment(e.nextDueDate).diff(today, 'days') <= 30)
      .sort((a, b) => new Date(a.nextDueDate) - new Date(b.nextDueDate));
    return { overdueVaccines: overdue, dueSoonVaccines: next };
  }, [vaccinations, isExpired, today, t]);

  if (vaccinationsQuery.isLoading) {
    return <CardSkeleton count={3} />;
  }

  if (vaccinationsQuery.isError) {
    return <ErrorState />;
  }

  if ((vaccinations || []).length === 0) {
    return (
      <NoEntries
        title={t('common.noVaccinationTitle')}
        description={t('common.noVaccinationDesc')}
        icon="vaccination"
      />
    );
  }

  return (
    <Box>
      
      
      {/* Search and Filters */}
      {(
        <SearchField
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onClear={() => setSearchTerm('')}
          placeholder={t('vaccination.searchPlaceholder')}
          ariaLabel={t('vaccination.searchAriaLabel')}
        />
      )}

      {/* Summary sections */}
      {(
        <Box sx={{ width: '100%', display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 3 }}>
          <Paper sx={{ p: 0, borderRadius: 3, border: `1px solid ${AppStyles.colors.border.light}`, boxShadow: AppStyles.shadows.sm, overflow: 'hidden', width: '100%' }}>
            <Box sx={{
              px: 2.5, py: 1.5,
              background: `linear-gradient(0deg, rgba(255,255,255,0.06), rgba(255,255,255,0.06)), linear-gradient(135deg, ${AppStyles.colors.error.main} 0%, ${AppStyles.colors.primary.main} 100%)`,
              color: '#fff',
              borderBottom: `1px solid ${AppStyles.colors.border.light}`,
              display: 'flex', alignItems: 'center',
              borderTopLeftRadius: 12, borderTopRightRadius: 12
            }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#fff' }}>{t('vaccination.overdueVaccines')}</Typography>
            </Box>
            <Box sx={{ p: 2.5 }}>
              {overdueVaccines.length === 0 ? (
                <Typography variant="body2" color="text.secondary">{t('vaccination.none')}</Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                  {overdueVaccines.map((v, idx) => (
                      <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ color: AppStyles.colors.text.primary }}>{v.category}</Typography>
                      <Typography variant="body2" sx={{ color: AppStyles.colors.text.primary }}>{moment(v.nextDueDate).format('DD MMM YYYY')}</Typography>
      </Box>
                  ))}
                          </Box>
                            )}
                          </Box>
          </Paper>
          <Paper sx={{ p: 0, borderRadius: 3, border: `1px solid ${AppStyles.colors.border.light}`, boxShadow: AppStyles.shadows.sm, overflow: 'hidden', width: '100%' }}>
            <Box sx={{
              px: 2.5, py: 1.5,
              background: `linear-gradient(0deg, rgba(255,255,255,0.06), rgba(255,255,255,0.06)), linear-gradient(135deg, ${AppStyles.colors.secondary.main} 0%, ${AppStyles.colors.primary.main} 100%)`,
              color: '#fff',
              borderBottom: `1px solid ${AppStyles.colors.border.light}`,
              display: 'flex', alignItems: 'center',
              borderTopLeftRadius: 12, borderTopRightRadius: 12
            }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#fff' }}>{t('vaccination.nextVaccines')}</Typography>
            </Box>
            <Box sx={{ p: 2.5 }}>
              {dueSoonVaccines.length === 0 ? (
                <Typography variant="body2" color="text.secondary">{t('vaccination.none')}</Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                  {dueSoonVaccines.map((v, idx) => (
                    <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" sx={{ color: AppStyles.colors.text.primary }}>{v.category}</Typography>
                      <Typography variant="body2" sx={{ color: AppStyles.colors.text.primary }}>{moment(v.nextDueDate).format('DD MMM YYYY')}</Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
            </Paper>
      </Box>
      )}

      {/* Category Cards - one per vaccination category (hide when no search results) */}
      {filteredVaccinations.length === 0 ? (
        <Paper sx={{ p: { xs: 2, md: 4 }, textAlign: 'center', mb: 3 }}>
          <Typography variant="h6" color="text.secondary">
            {t('vaccination.noResultsFound')}
              </Typography>
        </Paper>
      ) : (
        <Box sx={{ width: '100%', display: 'grid', gridTemplateColumns: '1fr', gap: 2, mb: 3 }}>
          {groupedByCategory.map((group) => (
            <Paper key={group.category} sx={{ p: 0, borderRadius: 3, border: `1px solid ${AppStyles.colors.border.light}`, boxShadow: AppStyles.shadows.sm, overflow: 'hidden', width: '100%' }}>
              <Box sx={{
                px: 2.5, py: 1.25,
                background: `linear-gradient(135deg, ${AppStyles.colors.primary.main}1A 0%, ${AppStyles.colors.secondary.main}1A 100%)`,
                color: AppStyles.colors.text.primary,
                borderBottom: `1px solid ${AppStyles.colors.border.light}`,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                borderTopLeftRadius: 12, borderTopRightRadius: 12
              }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: AppStyles.colors.text.primary }}>{group.category}</Typography>
              </Box>
              <Box sx={{ p: 2.5 }}>
                {group.items.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">{t('vaccination.noEntries')}</Typography>
                ) : (
                  <TableContainer sx={{ width: '100%' }}>
                    <Table sx={{ width: '100%' }}>
                      <TableHead>
                        <TableRow sx={{ '& th': { color: AppStyles.colors.text.secondary, fontWeight: 500, fontSize: '0.7rem', opacity: 0.7 } }}>
                          <TableCell>{t('vaccination.vaccineName')}</TableCell>
                          <TableCell>{t('vaccination.type')}</TableCell>
                          <TableCell>{t('vaccination.dateGiven')}</TableCell>
                          <TableCell>{t('vaccination.nextDue')}</TableCell>
                          <TableCell align="center">{t('vaccination.status')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {group.items.map((item, idx) => {
                          const renewedStyle = {
                            label: t('vaccination.renewed'),
                            bg: 'rgba(0,0,0,0.06)',
                            fg: AppStyles.colors.text.secondary,
                            border: 'rgba(0,0,0,0.15)'
                          };
                          const { label, bg, fg, border } = item._isRenewed ? renewedStyle : getStatusStyle(item.nextDueDate);
                          return (
                            <TableRow 
                              key={item.id || idx}
                              sx={{
                                '& td': {
                                  borderBottom: `1px solid ${AppStyles.colors.border.light}`,
                                },
                              }}
                            >
                              <TableCell>
                                <Typography variant="body2" sx={{ color: AppStyles.colors.text.primary, fontWeight: 600 }}>{item.vaccineName}</Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" color="text.secondary">{item.vaccineAdministrationType || '-'}</Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">{item.dateGiven ? moment(item.dateGiven).format('DD MMM YYYY') : '-'}</Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">{item.nextDueDate ? moment(item.nextDueDate).format('DD MMM YYYY') : '-'}</Typography>
                              </TableCell>
                              <TableCell align="center">
                                <Chip 
                                  label={label}
                                  size="small"
                                  sx={{
                                    backgroundColor: bg,
                                    color: fg,
                                    border: `1px solid ${border}`,
                                    fontWeight: 700,
                                    borderRadius: 999,
                                    boxShadow: '0 1px 2px rgba(0,0,0,0.06)'
                                  }}
                                />
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            </Paper>
          ))}
                  </Box>
      )}
    </Box>
  );
}

export default Vaccination;
